import { keepPreviousData, useQuery } from '@tanstack/react-query'
import _ from 'lodash'
import { Building, CircleUserRound, FilesIcon, LogOut, UserPen } from 'lucide-react'
import { observer } from 'mobx-react'
import { useContext, useMemo } from 'react'
import { Link } from 'react-router'

import icon from '@/assets/images/icon.png'
import { Avatar, AvatarFallback } from '@/components/elements/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/elements/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
} from '@/components/elements/select'
import { getAllEntities } from '@/services/entities.service'
import { logout, updateUser } from '@/services/user.service'
import { RootStoreContext } from '@/stores/RootStore'
import { UserStoreContext } from '@/stores/UserStore'

const MobileHeader = observer(() => {
  const { activeEntity, refreshToken, user, setUser } = useContext(UserStoreContext)
  const { clearStore } = useContext(RootStoreContext)

  // Fetch all entities for admins
  const { data: entities } = useQuery({
    queryKey: ['all-entities'],
    queryFn: () => getAllEntities(),
    placeholderData: keepPreviousData,
    enabled: user?.isXverseAdmin,
  })

  // Entity selector options
  const OPTIONS = useMemo(() => {
    const options = {}

    if (user?.isXverseAdmin) {
      options.Admin = [{ id: 'xverse-admin', label: 'Xverse Admin' }]
    }

    if (user?.entityRoles?.length > 0) {
      const userEntities = _.uniqBy(
        user.entityRoles.map((role) => role.entity),
        'id',
      )
      options['Your Accounts'] = userEntities.map((entity) => ({
        id: entity.id,
        label: entity.name,
      }))
    }

    if (user?.isXverseAdmin) {
      options['Other Accounts'] = (entities || [])
        .filter((entity) => !options['Your Accounts']?.some((o) => o.id === entity.id))
        .map((entity) => ({ id: entity.id, label: entity.name }))
    }

    return options
  }, [user, entities])

  const selectedValue = useMemo(() => {
    if (user?.isXverseAdmin && activeEntity === null) return 'xverse-admin'
    if (activeEntity) return activeEntity.id || activeEntity
    return null
  }, [user?.isXverseAdmin, activeEntity])

  const activeEntityDetails = useMemo(() => {
    if (!selectedValue) return null
    if (selectedValue === 'xverse-admin') return { name: 'Xverse Admin' }
    const fromRoles = user?.entityRoles?.find((r) => r.entity?.id === selectedValue)?.entity
    if (fromRoles) return fromRoles
    return entities?.find((e) => e.id === selectedValue) || null
  }, [selectedValue, user?.entityRoles, entities])

  const handleEntityChange = async (v) => {
    const updatedUser = { id: user.id }
    if (v === 'xverse-admin') {
      updatedUser.activeEntity = null
    } else {
      updatedUser.activeEntity = v
    }
    const response = await updateUser(updatedUser)
    setUser(response)
  }

  const hasName = user?.firstName && user?.lastName
  const initials = hasName ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}` : null

  const entityLogo = activeEntityDetails?.logo ? (
    <img
      src={activeEntityDetails.logo}
      alt={activeEntityDetails.name}
      className="size-7 rounded object-contain"
    />
  ) : (
    <Building className="size-7" />
  )

  return (
    <div className="flex h-14 shrink-0 items-center justify-between gap-2 px-3 md:hidden">
      {/* Left: Entity switcher */}
      <Select onValueChange={handleEntityChange} value={selectedValue}>
        <SelectTrigger
          className="h-auto w-auto border-0 bg-transparent p-0 shadow-none"
          showIcon={false}
          aria-label="Switch account"
        >
          {entityLogo}
        </SelectTrigger>
        <SelectContent>
          {Object.entries(OPTIONS).map(([groupLabel, groupOptions]) => (
            <SelectGroup key={groupLabel}>
              <SelectLabel>{groupLabel}</SelectLabel>
              {groupOptions.map((option) => (
                <SelectItem key={option.id} value={option.id}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>

      {/* Center: Logo */}
      <img src={icon} alt="RescueX" className="h-7" />

      {/* Right: User avatar with dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger className="outline-none" aria-label="User menu">
          <Avatar className="size-8 cursor-pointer text-xs">
            <AvatarFallback>
              {initials || <CircleUserRound className="stroke-primary size-5 stroke-1" />}
            </AvatarFallback>
          </Avatar>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-48 rounded-lg">
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col gap-0.5">
              {hasName && (
                <span className="text-sm font-medium">
                  {user.firstName} {user.lastName}
                </span>
              )}
              <span className="text-muted-foreground text-xs">{user?.email}</span>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem asChild>
              <Link to="/profile">
                <UserPen />
                Profile
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/certificates">
                <FilesIcon />
                My Certificates
              </Link>
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => logout(refreshToken, clearStore)}>
            <LogOut />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
})

export default MobileHeader
